import React, { useState } from 'react';
import { generateMockAnswers } from '../../../utils/aiMockDataGenerator';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle, MessageSquare, Edit2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const Step5_MockAnswersGenerator = ({ wizardData, updateData }) => {
  const { profile, parentInsights, questionSet, initialAnswers, selectedAccount } = wizardData;
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const generateAnswers = async () => {
    setGenerating(true);
    setError(null);

    try {
      const answers = await generateMockAnswers(questionSet, profile, parentInsights);
      updateData('initialAnswers', answers);

      // Save to database if we have a selected account
      if (selectedAccount) {
        try {
          const userId = selectedAccount.auth_user_id || selectedAccount.id;

          // Check if assessment_responses record exists
          const { data: existingResponse } = await supabase
            .from('assessment_responses')
            .select('id')
            .eq('user_id', userId)
            .single();

          const assessmentData = {
            user_id: userId,
            assessment_type: 'enhanced',
            responses: answers,
            initial_questions: questionSet || [],
            updated_at: new Date().toISOString()
          };

          if (existingResponse) {
            // Update existing record
            await supabase
              .from('assessment_responses')
              .update(assessmentData)
              .eq('user_id', userId);
          } else {
            // Insert new record
            await supabase
              .from('assessment_responses')
              .insert(assessmentData);
          }

          console.log('✅ Saved initial answers to database');
        } catch (dbError) {
          console.error('Error saving to database:', dbError);
          // Don't throw - answers were still generated
        }
      }
    } catch (err) {
      console.error('Error generating answers:', err);
      setError('Failed to generate mock answers. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const updateAnswer = (questionKey, value) => {
    updateData('initialAnswers', {
      ...initialAnswers,
      [questionKey]: value
    });
  };

  const hasAnswers = initialAnswers && Object.keys(initialAnswers).length >= 8;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Mock Answers Generator</h2>
        <p className="text-slate-600">
          Generate realistic student responses to the assessment questions based on {profile.name}&#39;s profile.
        </p>
      </div>

      {!hasAnswers && (
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-300 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Generate Mock Answers</h3>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
            AI will create authentic student responses based on the profile, parent insights, and strategic context.
          </p>
          <button
            onClick={generateAnswers}
            disabled={generating}
            className="px-8 py-4 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-semibold text-lg disabled:opacity-50 flex items-center gap-3 mx-auto"
          >
            {generating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating Answers...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Mock Answers
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-1">Generation Failed</h4>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {hasAnswers && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-900 mb-1">Answers Generated!</h4>
              <p className="text-sm text-emerald-700">
                Review and edit the answers below before proceeding.
              </p>
            </div>
            <button
              onClick={generateAnswers}
              disabled={generating}
              className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              Regenerate All
            </button>
          </div>

          <div className="space-y-4">
            {questionSet.map((q, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center text-violet-700 font-bold">
                      {index + 1}
                    </div>
                    <h4 className="font-semibold text-slate-900">Question {index + 1}</h4>
                  </div>
                  <button
                    onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                    className="text-violet-600 hover:text-violet-800 flex items-center gap-2 text-sm"
                  >
                    <Edit2 className="w-4 h-4" />
                    {editingIndex === index ? 'Done' : 'Edit Answer'}
                  </button>
                </div>

                <p className="text-slate-700 mb-4 leading-relaxed bg-slate-50 p-3 rounded-lg">{q.question}</p>

                {editingIndex === index ? (
                  <textarea
                    value={initialAnswers[`q${index + 1}`] || ''}
                    onChange={(e) => updateAnswer(`q${index + 1}`, e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-violet-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                    placeholder="Student's answer..."
                  />
                ) : (
                  <div className="bg-violet-50 rounded-lg p-4">
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {initialAnswers[`q${index + 1}`] || 'No answer yet'}
                    </p>
                  </div>
                )}

                <div className="mt-2 text-xs text-slate-500">
                  {initialAnswers[`q${index + 1}`]?.length || 0} characters
                </div>
              </div>
            ))}
          </div>

          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-azure-900 mb-1">Next Step: Follow-up Questions</h4>
                <p className="text-sm text-azure-700">
                  Based on these answers, AI will generate personalized follow-up questions (9-15).
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step5_MockAnswersGenerator;
